import React from 'react'
import styled from 'styled-components'
import Head from 'next/head'
import Router from 'next/router'

import Layout from '../../Layout/Layout'
import { SCREEN_SIZE } from '../../../lib/Media'
import { BenefitRowContent, DigitalClubErrorModal } from '../HelperComponents'
import { getAllDcProgrammeDetails } from '../../../lib/leapFetcher'
import { trackPageViewedEvent } from '../../../lib/analytics/events/page/PageViewedEvent'
import {
  Text,
  Title,
  Link,
  ArrowRightIcon,
  Price,
  IconButton,
} from '@ntuctech/tangram-web'
import { trackDigitalClubButtonClickedEvent } from '../../../lib/analytics/events/buttonClickEvents/DigitalClubButtonClickedEvent'

const StyledLayout = styled(Layout)`
  background-color: white;
  & main {
    ${SCREEN_SIZE.Below.Tablet} {
      padding: 0;
    }
  }
`

const SubtitleText = styled.div`
  margin-bottom: 2rem;
`
const ChosePlanText = styled.div`
  margin-bottom: 1rem;
  @media (max-width: 768px) {
    margin-top: 2.25rem;
  }
`

const EntryPointWrapper = styled.div`
  margin-top: 1.5rem;
  @media (min-width: 769px) {
    gap: 1rem;
    display: flex;
    justify-content: center;
  }
  @media (max-width: 768px) {
    text-align: center;
  }
`
const HeaderBanner = styled.div`
  @media (min-width: 1025px) {
    margin: 0 auto;
    background-image: url('https://media.nedigital.sg/fairprice/files/866f9415-1b94-4905-b723-e4521d62239e/DC_Header_Illustration.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 100%;
    max-width: 59rem;
    height: 10rem;
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    background-image: url('https://media.nedigital.sg/fairprice/files/cfce2086-0ec9-4fa0-8b23-010835b25ad5/DC_Header_Illustration_tablet.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 100%;
    max-width: 64rem;
    height: 12.5rem;
  }

  @media (max-width: 768px) {
    background-image: url('https://media.nedigital.sg/fairprice/files/a6b34a41-75b0-4173-bcaf-d4567b6d6dc2/DC_Header_Illustration_mobile.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 100%;
    max-width: 48rem;
    height: 13.125rem;
  }
`
const WrapperContentSection = styled.div`
  @media (min-width: 1025px) {
    padding: ${props => (props.standAlonePage ? '0 2.5rem' : '0 1rem')};
    padding-bottom: ${props => !props.standAlonePage && '2rem'};
  }
`

const PlanDetailWrapper = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  width: 22rem;
  border: 1px solid #cccccc;
  border-radius: 1rem;
`

const PriceAndButtonLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`

const TitleAndHighlightLine = styled.div`
  display: flex;
  justify-content: space-between;
`

const PlanHighlight = styled.div`
  background-color: ${props => props.bgColor};
  padding: 0.125rem 0.375rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
`

const HighlightText = styled(Text)`
  color: #ffffff;
`

const PlanAndBenefitWrapper = styled.div`
  @media (max-width: 768px) {
    display: grid;
    place-items: center;
  }
`

const EntryPointContentWrapper = styled.div`
  background-color: ${props => !props.standAlonePage && '#FFFFFF'};
  padding: ${props => !props.standAlonePage && '0 1rem'};
`

const getChosePlanTrackingParam = planId => {
  return {
    BUTTON_NAME: planId === 5 ? 'monthly plan' : 'annual plan',
    MEMBERSHIP_STATUS: 'inactive',
    CURRENT_PLAN_TYPE: null,
  }
}

const PlanDetails = ({
  planDetails,
  isFreeTrialEligible,
  isAnnualMembershipTrackingEnabled,
}) => {
  return (
    planDetails &&
    planDetails.map(plan => {
      const trackingParam = getChosePlanTrackingParam(plan.id)
      return (
        <PlanDetailWrapper key={plan.id}>
          <TitleAndHighlightLine>
            <div>
              <Title size="s">{plan.ui.title}</Title>
            </div>
            {plan.ui.highlight && (
              <PlanHighlight bgColor={plan.ui.highlight.bgColor}>
                <HighlightText size="s">{plan.ui.highlight.text}</HighlightText>
              </PlanHighlight>
            )}
          </TitleAndHighlightLine>
          <PriceAndButtonLine>
            <div>
              <Price
                amount={plan.ui.priceBreakdown.split('$')[1]}
                size="xl"
                style={{ marginRight: '0.375rem' }}
              />
              <Text size="s" bold>
                {plan.ui.priceBreakdownText || 'per month'}
              </Text>
            </div>
            <IconButton
              onClick={() => {
                isAnnualMembershipTrackingEnabled &&
                  trackDigitalClubButtonClickedEvent(trackingParam)
                Router.push(
                  `/membership/checkout?planId=${plan.id}&isFreeTrialEligible=${isFreeTrialEligible}`
                )
              }}
            >
              <ArrowRightIcon />
            </IconButton>
          </PriceAndButtonLine>
          <Text size="s">{plan.ui.pricePerYear}</Text>
        </PlanDetailWrapper>
      )
    })
  )
}

const BenefitRow = ({ benefits }) => {
  return (
    benefits &&
    benefits.map(benefit => {
      return <BenefitRowContent benefit={benefit} key={benefit.description} />
    })
  )
}

const EntryPointContent = ({
  standAlonePage,
  isAnnualMembershipTrackingEnabled,
}) => {
  const [programDetails, setProgramDetails] = React.useState({})
  const [openErrModal, setOpenErrModal] = React.useState(false)
  const [err, setErr] = React.useState(null)

  React.useEffect(() => {
    isAnnualMembershipTrackingEnabled &&
      trackPageViewedEvent({
        MEMBERSHIP_STATUS: 'inactive',
        CURRENT_PLAN_TYPE: null,
        SCREEN_NAME: 'membership | digital club landing page',
      })
    const getDcProgDetails = async () => {
      const details = await getAllDcProgrammeDetails().catch(() => {
        setErr({
          title: 'Failed to load program',
          message: 'Something went wrong',
        })
        setOpenErrModal(true)
      })
      setProgramDetails(details)
    }
    getDcProgDetails()
  }, [])
  return (
    <EntryPointContentWrapper standAlonePage={standAlonePage}>
      <HeaderBanner />
      <EntryPointWrapper>
        <WrapperContentSection standAlonePage={standAlonePage}>
          <Title size="l">{programDetails?.title}</Title>
          <SubtitleText>
            <Text size="s">{programDetails?.description}</Text>
          </SubtitleText>
          <PlanAndBenefitWrapper>
            <BenefitRow benefits={programDetails?.benefitsV2} />
          </PlanAndBenefitWrapper>
        </WrapperContentSection>
        <WrapperContentSection standAlonePage={standAlonePage}>
          <ChosePlanText>
            <Title size="l">Choose your plan</Title>
          </ChosePlanText>
          <PlanAndBenefitWrapper>
            <PlanDetails
              planDetails={programDetails?.packages}
              isFreeTrialEligible={programDetails?.isFreeTrialEligible}
              isAnnualMembershipTrackingEnabled={
                isAnnualMembershipTrackingEnabled
              }
            />
          </PlanAndBenefitWrapper>
          <Text size="s">
            {programDetails?.faq?.text?.split('~~FAQ.~~')}{' '}
            <Link href={programDetails?.faq?.url}>FAQ</Link>.
          </Text>
        </WrapperContentSection>
      </EntryPointWrapper>
      <DigitalClubErrorModal
        open={openErrModal}
        setOpen={setOpenErrModal}
        errorTitle={err?.title}
        errorMessage={err?.message}
        setErr={setErr}
      />
    </EntryPointContentWrapper>
  )
}

const DigitalClubEntryPointNew = props => {
  const {
    organizationData,
    categoryList,
    standAlonePage,
    isAnnualMembershipTrackingEnabled,
  } = props
  return (
    <div>
      <Head>
        <meta
          name="description"
          key="meta_description"
          content="Enjoy low prices and access online-exclusive products and offers! Get $10 off your first online order and enjoy free delivery with min. spend $59."
        />
        <title>NTUC FairPrice | Membership Registration</title>
      </Head>
      {standAlonePage ? (
        <StyledLayout
          organizationData={organizationData}
          categoryList={categoryList}
          showPostalCodeModule
        >
          <EntryPointContent
            standAlonePage={standAlonePage}
            isAnnualMembershipTrackingEnabled={
              isAnnualMembershipTrackingEnabled
            }
          />
        </StyledLayout>
      ) : (
        <EntryPointContent
          standAlonePage={standAlonePage}
          isAnnualMembershipTrackingEnabled={isAnnualMembershipTrackingEnabled}
        />
      )}
    </div>
  )
}

export default DigitalClubEntryPointNew
